import { LocationDetails, User, userQueryKey, useUserPutMutation } from '@alluradirect/react';
import { Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import ConfirmationDialog from 'components/core/ConfirmationDialog/ConfirmationDialog';
import Grid from 'components/core/Grid';
import { FormikAddress } from 'components/formik/core/FormikAddress/FormikAddress';
import FormikDateOfBirth, {
  generateDOB,
  generateInitialDateOfBirth,
} from 'components/formik/core/FormikDateOfBirth/FormikDateOfBirth';
import { AccountOwnerName } from 'components/views/forms/AccountOwner/components/AccountOwnerName';
import { AccountOwnerPhone } from 'components/views/forms/AccountOwner/components/AccountOwnerPhone';
import { Formik, Form } from 'formik';
import { UserMissingProfileInfoReturn } from 'helpers/userHelpers';
import { useMemo } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from 'react-query';
import { generateMissingInfoSchema } from 'components/views/pages/Checkout/CheckoutStep1/components/CheckoutMissingInfoDialog/helpers';

interface MissingInfoValues extends User {
  DOB: {
    YEAR: any;
    MONTH: any;
    DAY: any;
  };
  DATE_OF_BIRTH: string;
}

interface Props {
  isOpen: boolean;
  close: () => void;
  missingInfo: UserMissingProfileInfoReturn;
  user: User;
  locationDetails: LocationDetails | undefined;
}

export const CheckoutMissingInfoDialog = ({ isOpen, close, missingInfo, user, locationDetails }: Props) => {
  const qClient = useQueryClient();
  const updateUser = useUserPutMutation();

  const onSubmit = async (vals: MissingInfoValues, helpers: any) => {
    try {
      await updateUser.mutateAsync({
        ...vals,
        ...vals.ADDRESS,
      });
      await qClient.refetchQueries(userQueryKey);

      close();
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const allMissingInfo = missingInfo;

  const validationSchema = generateMissingInfoSchema(allMissingInfo);

  const initialValues = useMemo(() => {
    const { DOB } = generateDOB(user.DATE_OF_BIRTH);
    const dateOfBirth = generateInitialDateOfBirth(DOB.YEAR, DOB.MONTH, DOB.DAY);

    return {
      ...user,
      DOB,
      DATE_OF_BIRTH: dateOfBirth,
    };
  }, [user]);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ handleSubmit, isSubmitting, values }) => (
        <Form>
          <ConfirmationDialog
            dialogTitle={'Add Missing Information'}
            isDialogOpen={isOpen}
            handleCloseDialog={close}
            handleClickConfirm={handleSubmit}
            isLoading={isSubmitting}
          >
            <Grid container spacing={2} id="action_new_guest">
              <Grid item xs={12}>
                <Typography variant="h6">First and Last Name</Typography>
              </Grid>
              <AccountOwnerName formikPrefix={''} isCompany={false} />
              <Grid item xs={12}>
                <AppDivider marginVertical={'12px'} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Verify Phone</Typography>
              </Grid>
              <AccountOwnerPhone
                formikPrefix={''}
                isSmsVerified={values.IS_SMS_VERIFIED}
                userId={user.ID}
                initialPhoneData={values.PHONE}
              />

              <Grid item xs={12}>
                <AppDivider marginVertical={'12px'} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Address</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  * Your home address should match what is listed on your government issued identification. Some
                  properties may have a check-in procedure that verifies your identification and home address.
                </Typography>
              </Grid>
              <FormikAddress formikPrefix={''} locationDetails={locationDetails} />

              <Grid item xs={12}>
                <AppDivider marginVertical={'12px'} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Date of Birth</Typography>
                <FormikDateOfBirth fieldPrefix={'DOB'} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  * Date of birth may be required for properties that have a minimum age restriction, but is not
                  directly shared with the host. This age must match your government issued identification.
                </Typography>
              </Grid>
            </Grid>
          </ConfirmationDialog>
        </Form>
      )}
    </Formik>
  );
};
